import { Controls } from '../systems/Controls';
import { GamepadConfig, MinimalPad, loadConfig, readAxis, readButton, readPedalAxis } from './GamepadConfig';
import { WebHidInput } from './WebHidInput';

export type InputMode = 'gamepadapi' | 'webhid';
const MODE_KEY = 'ifrtrainer.inputMode.v1';

function loadMode(): InputMode {
  try {
    const v = localStorage.getItem(MODE_KEY);
    return v === 'webhid' ? 'webhid' : 'gamepadapi';
  } catch {
    return 'gamepadapi';
  }
}

/**
 * Joystick / gamepad input, driven by a user-configurable binding (see
 * GamepadConfig / the Ctrl+J config UI). Two input sources are available,
 * user-selectable (persisted), because the standard Gamepad API has a real,
 * well-documented ~4-simultaneous-device cap on Chromium/Windows (it
 * multiplexes XInput+DirectInput into one device list) that no web page can
 * lift:
 *  - 'gamepadapi' (default): navigator.getGamepads() — zero setup, but capped.
 *  - 'webhid': navigator.hid — a device picker grants per-device access, no
 *    device-count cap, at the cost of that manual grant step (see WebHidInput).
 *
 * Multiple USB controllers are supported within a source: every binding (each
 * axis, each button) records which physical device it came from, so e.g.
 * aileron/elevator can be bound on one stick and rudder pedals or a separate
 * throttle quadrant on another — poll() reads each control from whichever
 * device its own binding points at, not just "the first pad".
 *
 * If a joystick is present AND actually being engaged, it overrides the keyboard
 * targets for that frame. A pad that reports but sits idle at rest (e.g. a
 * virtual-joystick driver such as vJoy, which the browser can see even when
 * nothing is touching it) must NOT silently zero out keyboard input every frame,
 * so poll() only writes to the shared controls when it detects genuine
 * engagement (an axis away from centre, or a button pressed) on ANY connected
 * device.
 */
export class Gamepad {
  connected = false;
  config: GamepadConfig = loadConfig();
  /** When true (e.g. the binding UI is open) the pad won't drive the sim. */
  suspended = false;
  mode: InputMode = loadMode();
  readonly hid = new WebHidInput();

  constructor(private controls: Controls) {
    window.addEventListener('gamepadconnected', (e) => {
      // eslint-disable-next-line no-console
      console.info('Joystick detected:', (e as GamepadEvent).gamepad.id);
    });
    window.addEventListener('gamepaddisconnected', () => {
      this.connected = false;
    });
  }

  setMode(mode: InputMode): void {
    this.mode = mode;
    try { localStorage.setItem(MODE_KEY, mode); } catch { /* ignore */ }
  }

  /** Opens the browser's HID device picker (must be called from a user
   * gesture, e.g. a button click) so the user can grant access to a
   * controller that the Gamepad API's device cap is hiding. */
  requestHidDevice(): Promise<void> {
    return this.hid.requestDevice();
  }

  /** Re-read the persisted config (call after the binding UI saves). */
  reloadConfig(): void {
    this.config = loadConfig();
  }

  /** Every device slot from the active input source (sparse for the native
   * Gamepad API — disconnected slots are null). Indices here are exactly the
   * `pad` index bindings use. */
  getPads(): (MinimalPad | null)[] {
    if (this.mode === 'webhid') return this.hid.getPads();
    return navigator.getGamepads ? Array.from(navigator.getGamepads()) : [];
  }

  /** The first connected pad, or null — used only where "is anything plugged
   * in at all" is the question (e.g. the config UI's live axis preview). */
  getPad(): MinimalPad | null {
    for (const p of this.getPads()) if (p) return p;
    return null;
  }

  /** Returns true if a pad actually drove the controls this frame. */
  poll(): boolean {
    if (this.suspended) return false;
    const pads = this.getPads();
    if (!pads.some((p) => p !== null)) return false;

    const cfg = this.config;
    const c = this.controls;
    const dz = cfg.deadzone;
    const aileron = readAxis(cfg.aileron, dz, pads);
    const elevator = readAxis(cfg.elevator, dz, pads);
    const rudder = readAxis(cfg.rudder, dz, pads);

    const thrBind = cfg.throttle;
    const thrPad = thrBind ? pads[thrBind.pad] : null;
    const thrAxis = thrBind && thrPad && thrBind.axis < thrPad.axes.length ? thrPad.axes[thrBind.axis] : null;
    const throttleActive = thrAxis !== null && Math.abs(thrAxis) > 0.02;

    const anyButtonPressed = pads.some((p) => p?.buttons.some((b) => b.pressed));
    const anyAxisActive = aileron !== 0 || elevator !== 0 || rudder !== 0 || throttleActive;

    if (!anyAxisActive && !anyButtonPressed) {
      // Idle/phantom pad — don't override keyboard control with a flat centre.
      return false;
    }
    this.connected = true;

    c.aileronTarget = aileron;
    c.elevatorTarget = elevator;
    c.rudderTarget = rudder;

    // Throttle lever: many sticks report −1 (full) .. +1 (idle); map to 0..1.
    if (throttleActive && thrAxis !== null && thrBind) {
      const v = thrBind.invert ? (1 + thrAxis) / 2 : (1 - thrAxis) / 2;
      c.throttle = v < 0 ? 0 : v > 1 ? 1 : v;
    }

    // Buttons
    const b = cfg.buttons;
    if (this.edge('flapsDown', readButton(b.flapsDown, pads))) c.flapsDown();
    if (this.edge('flapsUp', readButton(b.flapsUp, pads))) c.flapsUp();
    if (this.edge('gear', readButton(b.gear, pads))) c.toggleGear();
    // (Sign matches the corrected keyboard mapping in Keyboard.ts.)
    if (readButton(b.trimDown, pads)) c.adjustTrim(0.01);
    if (readButton(b.trimUp, pads)) c.adjustTrim(-0.01);
    c.brakes = readButton(b.brakes, pads) ? 1 : 0;

    // Independent per-wheel analog brake axes (toe-brake pedals), on top of
    // the digital brakes button above — combined per-wheel in FlightModel.
    c.brakeLeft = readPedalAxis(cfg.brakeLeft, pads);
    c.brakeRight = readPedalAxis(cfg.brakeRight, pads);

    return true;
  }

  private prev = new Map<string, boolean>();
  private edge(id: string, now: boolean): boolean {
    const was = this.prev.get(id) ?? false;
    this.prev.set(id, now);
    return now && !was;
  }
}
